import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const WebSocketContext = createContext(null);

// Get WebSocket URL from backend URL
const getWsUrl = () => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  // Convert http(s) to ws(s)
  return backendUrl.replace(/^http/, 'ws');
};

export const WebSocketProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [incomingCall, setIncomingCall] = useState(null);

  // Keep-alive ping interval
  const pingIntervalRef = useRef(null);

  const connect = useCallback(() => {
    if (!isAuthenticated || !user) return;
    
    // Close existing connection if any
    if (wsRef.current) {
      wsRef.current.close();
    }

    const wsUrl = `${getWsUrl()}/ws/${user.user_id}:${user.role}`;
    console.log('🔌 Connecting to WebSocket:', wsUrl);
    
    try {
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        console.log('✅ WebSocket connected');
        setIsConnected(true);
        
        // Start ping interval to keep connection alive
        pingIntervalRef.current = setInterval(() => {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send('ping');
          }
        }, 30000); // Ping every 30 seconds
      };

      wsRef.current.onmessage = (event) => {
        try {
          // Handle pong response
          if (event.data === 'pong') return;
          
          const data = JSON.parse(event.data);
          
          if (data.type === 'notification') {
            handleNotification(data);
          } else if (data.type === 'connection') {
            console.log('🔗 Connection confirmed:', data);
          }
        } catch (err) {
          console.error('Failed to parse WebSocket message:', err);
        }
      };

      wsRef.current.onclose = (event) => {
        console.log('🔌 WebSocket disconnected:', event.reason);
        setIsConnected(false);
        
        // Clear ping interval
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
        }
        
        // Attempt to reconnect after 5 seconds
        if (isAuthenticated && !event.wasClean) {
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log('🔄 Attempting to reconnect...');
            connect();
          }, 5000);
        }
      };

      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
    } catch (error) {
      console.error('Failed to create WebSocket:', error);
    }
  }, [isAuthenticated, user]);

  const handleNotification = useCallback((notification) => {
    // Add to notifications list
    setNotifications(prev => [notification, ...prev].slice(0, 50)); // Keep last 50
    setUnreadCount(prev => prev + 1);
    
    const { title, data, priority, event } = notification;
    const message = data?.message || title;
    
    // Special handling for incoming calls and SMS
    if (event === 'incoming_call' || event === 'incoming_sms') {
      setIncomingCall(notification);
      return; // Don't show regular toast for these
    }
    
    // Show toast based on priority
    if (priority === 'high' || priority === 'urgent') {
      toast.success(message, {
        description: data?.work_order_id || data?.assignment_id,
        duration: 8000,
      });
    } else if (priority === 'medium') {
      toast.info(message, {
        description: data?.work_order_id || data?.assignment_id,
        duration: 5000,
      });
    } else {
      toast(message, {
        duration: 4000,
      });
    }
  }, []);

  const dismissIncomingCall = useCallback(() => {
    setIncomingCall(null);
  }, []);

  const markAllRead = useCallback(() => {
    setUnreadCount(0);
  }, []);

  const clearNotifications = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
  }, []);

  // Connect when user is authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      connect();
    }
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
    };
  }, [isAuthenticated, user, connect]);

  const value = {
    isConnected,
    notifications,
    unreadCount,
    markAllRead,
    clearNotifications,
    incomingCall,
    dismissIncomingCall,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};
